import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Globe, Plus, Trash2, RefreshCw, TrendingUp, TrendingDown, ExternalLink,
  Loader2, BarChart3, Eye, Zap, ChevronDown, ChevronUp, AlertTriangle, Lock
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const PLAN_FEATURES = {
  free: { domains: 1, label: "Free" },
  pro: { domains: 5, label: "Pro" },
  business: { domains: 20, label: "Business" },
  enterprise: { domains: 999, label: "Enterprise" },
};

export default function MyDomainsTab({ user, t }) {
  const [domains, setDomains] = useState([]);
  const [limit, setLimit] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addUrl, setAddUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [domainData, setDomainData] = useState({});
  const [loadingScans, setLoadingScans] = useState({});

  const plan = user?.plan || "free";
  const planInfo = PLAN_FEATURES[plan] || PLAN_FEATURES.free;

  const fetchDomains = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/domains`, { withCredentials: true });
      setDomains(res.data.domains || []);
      setLimit(res.data.limit || 1);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addUrl.trim()) return;
    setAdding(true);
    setError("");
    try {
      await axios.post(`${API}/domains`, { url: addUrl.trim() }, { withCredentials: true });
      setAddUrl("");
      await fetchDomains();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
    setAdding(false);
  };

  const handleDelete = async (domainId) => {
    try {
      await axios.delete(`${API}/domains/${domainId}`, { withCredentials: true });
      setDomains(prev => prev.filter(d => d.domain_id !== domainId));
      if (expandedDomain === domainId) setExpandedDomain(null);
    } catch (e) {
      console.error(e);
    }
  };

  const loadDomainScans = async (domainId) => {
    if (domainData[domainId]) return;
    setLoadingScans(prev => ({ ...prev, [domainId]: true }));
    try {
      const res = await axios.get(`${API}/domains/${domainId}/scans`, { withCredentials: true });
      setDomainData(prev => ({ ...prev, [domainId]: res.data }));
    } catch (e) {
      console.error(e);
    }
    setLoadingScans(prev => ({ ...prev, [domainId]: false }));
  };

  const toggleDomain = (domainId) => {
    if (expandedDomain === domainId) {
      setExpandedDomain(null);
    } else {
      setExpandedDomain(domainId);
      loadDomainScans(domainId);
    }
  };

  const dm = t.dm_title ? t : {};

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-domains-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black font-['Chivo']">{dm.dm_title || "My Domains"}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {domains.length}/{limit} {dm.dm_domains_used || "domains"} · {planInfo.label} {dm.dm_plan_label || "Plan"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {domains.length < limit && (
            <div className="h-2 w-24 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${(domains.length / limit) * 100}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* Add Domain */}
      {domains.length < limit ? (
        <form onSubmit={handleAdd} className="bg-white border border-gray-200 p-4 flex gap-3 items-end" data-testid="add-domain-form">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">{dm.dm_add_domain || "Add Domain"}</label>
            <input
              type="text"
              value={addUrl}
              onChange={(e) => setAddUrl(e.target.value)}
              placeholder="example.com"
              className="w-full px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
              data-testid="domain-url-input"
            />
          </div>
          <button
            type="submit"
            disabled={adding || !addUrl.trim()}
            className="px-5 py-2.5 bg-blue-600 text-white font-semibold text-sm hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            data-testid="add-domain-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {dm.dm_add_btn || "Add"}
          </button>
        </form>
      ) : (
        <div className="bg-amber-50 border border-amber-200 p-4 flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">{dm.dm_limit_reached || "Domain limit reached"}</p>
            <p className="text-xs text-amber-600">{dm.dm_upgrade_msg || "Upgrade your plan to add more domains."}</p>
          </div>
          <a href="/#pricing" className="ml-auto px-4 py-2 bg-amber-600 text-white text-xs font-bold hover:bg-amber-500 whitespace-nowrap">
            {dm.dm_upgrade_btn || "Upgrade"}
          </a>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Domain List */}
      {domains.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Globe className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">{dm.dm_empty || "No domains yet"}</h3>
          <p className="text-sm text-gray-500">{dm.dm_empty_desc || "Add your first domain to start tracking SEO performance."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {domains.map((domain) => (
            <div key={domain.domain_id} className="bg-white border border-gray-200 overflow-hidden" data-testid={`domain-${domain.domain_id}`}>
              {/* Domain Header */}
              <button
                onClick={() => toggleDomain(domain.domain_id)}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
                data-testid={`domain-toggle-${domain.domain_id}`}
              >
                <div className="w-10 h-10 bg-blue-100 flex items-center justify-center flex-shrink-0 rounded">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{domain.url.replace("https://", "").replace("http://", "")}</p>
                  <p className="text-xs text-gray-500">
                    {dm.dm_added || "Added"}: {new Date(domain.created_at).toLocaleDateString("sk-SK")}
                    {domain.last_scan && <> · {dm.dm_last_scan || "Last scan"}: {new Date(domain.last_scan).toLocaleDateString("sk-SK")}</>}
                  </p>
                </div>
                {domain.last_score !== null && domain.last_score !== undefined && (
                  <span className={`px-3 py-1.5 text-sm font-bold ${
                    domain.last_score >= 80 ? "bg-green-100 text-green-700" :
                    domain.last_score >= 60 ? "bg-yellow-100 text-yellow-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {domain.last_score}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <a href={domain.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                    className="p-1.5 text-gray-400 hover:text-blue-600">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(domain.domain_id); }}
                    className="p-1.5 text-gray-400 hover:text-red-600" data-testid={`delete-domain-${domain.domain_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedDomain === domain.domain_id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </button>

              {/* Expanded Content */}
              {expandedDomain === domain.domain_id && (
                <div className="border-t border-gray-200 p-5 space-y-6">
                  {loadingScans[domain.domain_id] ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : domainData[domain.domain_id] ? (
                    <DomainDetails data={domainData[domain.domain_id]} t={t} domain={domain} />
                  ) : (
                    <p className="text-center text-gray-500 py-8">{dm.dm_no_data || "No scan data yet. Run a scan from the homepage."}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DomainDetails({ data, t, domain }) {
  const { scans, score_history, total_scans } = data;
  const dm = t.dm_title ? t : {};

  const latestScan = scans?.[0];
  const prevScan = scans?.[1];

  const seoChange = latestScan && prevScan
    ? (latestScan.seo_score || latestScan.overall_score || 0) - (prevScan.seo_score || prevScan.overall_score || 0)
    : 0;

  const chartData = score_history?.map(h => ({
    date: h.date ? new Date(h.date).toLocaleDateString("sk-SK", { day: "2-digit", month: "2-digit" }) : "",
    seo: h.seo_score || 0,
    ai: h.ai_visibility || 0,
    perf: h.performance || 0,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-50 border border-gray-100 p-4 text-center">
          <BarChart3 className="w-5 h-5 text-blue-600 mx-auto mb-1" />
          <p className="text-2xl font-bold font-mono">{latestScan?.seo_score || latestScan?.overall_score || "—"}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">SEO Score</p>
          {seoChange !== 0 && (
            <p className={`text-xs font-bold flex items-center justify-center gap-0.5 mt-1 ${seoChange > 0 ? "text-green-600" : "text-red-600"}`}>
              {seoChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {seoChange > 0 ? "+" : ""}{seoChange}
            </p>
          )}
        </div>
        <div className="bg-gray-50 border border-gray-100 p-4 text-center">
          <Eye className="w-5 h-5 text-purple-600 mx-auto mb-1" />
          <p className="text-2xl font-bold font-mono">
            {latestScan?.ai_visibility?.overall_score || "—"}
          </p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">AI Visibility</p>
        </div>
        <div className="bg-gray-50 border border-gray-100 p-4 text-center">
          <Zap className="w-5 h-5 text-amber-600 mx-auto mb-1" />
          <p className="text-2xl font-bold font-mono">
            {latestScan?.performance?.score || "—"}
          </p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Performance</p>
        </div>
        <div className="bg-gray-50 border border-gray-100 p-4 text-center">
          <RefreshCw className="w-5 h-5 text-teal-600 mx-auto mb-1" />
          <p className="text-2xl font-bold font-mono">{total_scans || 0}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{dm.dm_total_scans || "Total Scans"}</p>
        </div>
      </div>

      {/* Score History Graph */}
      {chartData.length > 1 && (
        <div className="bg-white border border-gray-100 p-4">
          <h4 className="text-sm font-bold text-gray-700 mb-3">{dm.dm_score_trend || "Score Trend"}</h4>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="seoGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="aiGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#999" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#999" />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", fontSize: 12 }}
                  labelStyle={{ fontWeight: "bold" }}
                />
                <Area type="monotone" dataKey="seo" name="SEO" stroke="#3B82F6" fill="url(#seoGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="ai" name="AI Visibility" stroke="#8B5CF6" fill="url(#aiGrad)" strokeWidth={2} />
                <Line type="monotone" dataKey="perf" name="Performance" stroke="#F59E0B" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Scan History */}
      {scans?.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-gray-700 mb-3">{dm.dm_scan_history || "Scan History"}</h4>
          <div className="space-y-2">
            {scans.map((scan, i) => {
              const score = scan.seo_score || scan.overall_score || 0;
              return (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors">
                  <span className={`w-12 h-12 flex items-center justify-center font-bold font-mono text-lg ${
                    score >= 80 ? "bg-green-100 text-green-700" :
                    score >= 60 ? "bg-yellow-100 text-yellow-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {score}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{scan.url}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(scan.timestamp || scan.created_at).toLocaleDateString("sk-SK", {
                        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
                      })}
                    </p>
                  </div>
                  <div className="flex gap-3 text-xs text-gray-500">
                    {scan.ai_visibility?.overall_score !== undefined && (
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> AI: {scan.ai_visibility.overall_score}</span>
                    )}
                    {scan.performance?.score !== undefined && (
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> Perf: {scan.performance.score}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scan CTA */}
      <div className="flex justify-center pt-2">
        <a href="/" className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 flex items-center gap-2" data-testid="run-scan-btn">
          <RefreshCw className="w-4 h-4" /> {dm.dm_run_scan || "Run New Scan"}
        </a>
      </div>
    </div>
  );
}
